import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios"; // Your configured Axios instance
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

// Types
export interface Parameter {
  id: number;
  editable: boolean;
  supprimable: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * A parameter update.
 *
 * Both fields are optional so a caller can send ONLY the flag it changed.
 * Echoing back the other flag from a previously read value makes every toggle
 * a last-write-wins overwrite: two administrators on this screen at once, one
 * enables editing, the other enables deletion, and whoever saves second
 * silently reverts the first. The API accepts partial updates for exactly this
 * reason.
 */
export interface RequestParameter {
  editable?: boolean;
  supprimable?: boolean;
}

// React Query Keys
export const parameterKeys = {
  all: ["parameters"] as const,
  detail: () => ["parameters", "detail"] as const,
};

export const useGetParameter = () => {
  return useQuery<Parameter>({
    queryKey: parameterKeys.detail(),
    queryFn: async () => {
      const response = await axios.get("/parametre");
      return response.data.data;
    },
    // The API now creates the row on first read, so a 404 means the route is
    // wrong rather than the record being absent — retrying would not help.
    retry: (failureCount, error: any) =>
      error?.response?.status === 404 ? false : failureCount < 3,
  });
};

/**
 * Toggle a platform parameter, optimistically.
 *
 * Applied to the cache immediately and rolled back if the request fails. A
 * switch that waits for a round trip before moving reads as broken, and the
 * previous implementation was worse than that: it drove a full-screen blocking
 * loader, so flipping one switch greyed out the entire page.
 *
 * Rollback uses the snapshot captured in onMutate rather than a refetch, so a
 * failure restores the exact prior state even if the request never reached the
 * server.
 */
export const useUpdateParameter = (
  onClose?: () => void,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updatedParameter: RequestParameter) => {
      const response = await axios.put("/parametre", updatedParameter);
      return response.data;
    },

    onMutate: async (next) => {
      // Stop any in-flight read from landing after our optimistic write and
      // overwriting it with the pre-toggle value.
      await queryClient.cancelQueries({ queryKey: parameterKeys.detail() });

      const previous = queryClient.getQueryData<Parameter>(parameterKeys.detail());

      if (previous) {
        queryClient.setQueryData<Parameter>(parameterKeys.detail(), {
          ...previous,
          ...next,
        });
      }

      return { previous };
    },

    onError: (err: any, _vars, context) => {
      // Put the switch back where the user found it.
      if (context?.previous) {
        queryClient.setQueryData(parameterKeys.detail(), context.previous);
      }

      const status = err?.response?.status;

      // 403 is the API refusing the change, not a transient failure. It has a
      // specific cause — the account lacks platform.parameters.manage — and
      // saying so stops an operator retrying a button that will never work.
      if (status === 403 || status === 401) {
        toast.error(
          t(
            "parameter.unauthorizedUpdate",
            "You are not authorized to change platform parameters"
          )
        );
        return;
      }

      const errors = err?.response?.data?.errors;
      if (errors) {
        (Object.values(errors).flat() as string[]).forEach((m) => toast.error(m));
        return;
      }

      toast.error(
        err?.response?.data?.message ??
          t("parameter.updateError", "Error updating parameter")
      );
    },

    onSuccess: (data) => {
      onClose?.();
      onSuccess?.();

      // Server response is authoritative — it carries updated_at and any value
      // the server normalised.
      if (data?.data) {
        queryClient.setQueryData(parameterKeys.detail(), data.data);
      }

      toast.success(
        t("parameter.updateSuccess", "Parameter updated successfully")
      );
    },

    onSettled: () => {
      // Reconcile with the server whichever way it went. Placed here rather
      // than in onSuccess so a rolled-back failure also re-syncs, in case the
      // write actually landed before the connection dropped.
      queryClient.invalidateQueries({ queryKey: parameterKeys.all });
    },
  });
};
